/**
 * useAuthClaims.js
 *
 * Extracts all meaningful claims from the OIDC tokens.
 *
 * TOKEN STRUCTURE (WSO2 → Microsoft Entra / Azure AD):
 * ─────────────────────────────────────────────────────────────────────────
 * The WSO2-issued tokens contain basic OIDC claims:
 *   email, given_name, family_name, sub, org_id, org_name, aut, scope
 *
 * When Azure AD group IDs are included in the token they appear as:
 *   access_token  →  "groups": ["87222214-1b47-49b7-bed1-d6ad537ee9cd", ...]
 *
 * This hook decodes those claims and exposes them as structured values.
 * No code changes are needed once WSO2/Azure starts issuing group IDs —
 * the hook already reads them.
 *
 * FALLBACK STRATEGY (when no explicit roles/groups claim exists):
 * ─────────────────────────────────────────────────────────────────────────
 *   1. If `groups` claim exists in token  → use it
 *   2. If `roles`  claim exists in token  → use it
 *   3. Derive a broad role from `org_name` + email domain
 */

import { useAuth } from 'react-oidc-context';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Safely decode a JWT payload without verifying the signature.
 * Signature verification is the server's responsibility.
 * Client-side decoding is only for reading display/authorisation claims.
 */
function decodeJwtPayload(token) {
  try {
    if (!token) return {};
    const base64 = token.split('.')[1];
    if (!base64) return {};
    // base64url → base64
    const padded = base64.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
}

/**
 * Normalise a claim value that may be a string, string[], or undefined
 * into a clean string[].
 *
 * Handles:
 *   - undefined / null        → []
 *   - "GroupA,GroupB"         → ["GroupA", "GroupB"]
 *   - ["GroupA", "GroupB"]    → ["GroupA", "GroupB"]
 */
function normaliseToArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') return value.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

/**
 * Derive a broad access level from token claims when no explicit group is present.
 * This is a fallback — once Azure AD group IDs appear in the token, the
 * per-page group checks in useAuthorization.js take over.
 */
function deriveRoleFromClaims(claims) {
  const email   = (claims.email    || '').toLowerCase();
  const orgName = (claims.org_name || '').toLowerCase();
  const autType = (claims.aut      || '').toUpperCase(); // e.g. "APPLICATION_USER"

  // Explicit claims always win
  if (claims.roles && Array.isArray(claims.roles) && claims.roles.length > 0) {
    return claims.roles[0];
  }
  if (claims.groups && Array.isArray(claims.groups) && claims.groups.length > 0) {
    return claims.groups[0];
  }

  // Derive from WSO2 org/email
  if (orgName === 'super') return 'ADMIN';
  if (email.endsWith('@shrss.com') || email.endsWith('@hardrock.com')) return 'STAFF';
  if (autType === 'APPLICATION_USER') return 'USER';

  return 'GUEST';
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} AuthClaims
 * @property {string}   sub          - Subject (user ID)
 * @property {string}   email        - Email address
 * @property {string}   givenName    - First name
 * @property {string}   familyName   - Last name
 * @property {string}   fullName     - Full name
 * @property {string}   username     - Username / preferred_username
 * @property {string}   orgId        - WSO2 organisation ID
 * @property {string}   orgName      - WSO2 organisation name
 * @property {string[]} groups       - Group claims from token (Azure AD Object GUIDs or names)
 * @property {string[]} roles        - Role claims from token (WSO2 roles)
 * @property {string[]} scopes       - OAuth scopes granted
 * @property {string}   derivedRole  - Best-effort role when no explicit claim exists
 * @property {boolean}  isAdmin      - derivedRole === 'ADMIN'
 * @property {boolean}  isStaff      - derivedRole is ADMIN or STAFF
 */

/**
 * Returns structured claims extracted from the current OIDC user's tokens.
 * Falls back to empty/safe defaults when the user is not authenticated.
 *
 * @returns {AuthClaims}
 */
export function useAuthClaims() {
  const auth = useAuth();

  if (!auth?.user) {
    return {
      sub: '',
      email: '',
      givenName: '',
      familyName: '',
      fullName: '',
      username: '',
      orgId: '',
      orgName: '',
      groups: [],
      roles: [],
      scopes: [],
      derivedRole: 'GUEST',
      isAdmin: false,
      isStaff: false,
    };
  }

  // id_token claims — already parsed by oidc-client-ts
  const profile = auth.user.profile || {};

  // access_token claims — must be decoded manually
  const atClaims = decodeJwtPayload(auth.user.access_token);

  // Merge: access_token claims win over id_token where they overlap
  const combined = { ...profile, ...atClaims };

  // ── Standard claims ────────────────────────────────────────────────────────
  const sub        = combined.sub                                      || '';
  const email      = combined.email                                    || '';
  const givenName  = combined.given_name  || profile.given_name        || '';
  const familyName = combined.family_name || profile.family_name       || '';
  const fullName   = combined.name        || `${givenName} ${familyName}`.trim();
  const username   = combined.username    || combined.preferred_username || sub;
  const orgId      = combined.org_id                                   || '';
  const orgName    = combined.org_name                                 || '';

  // ── Group / role claims ────────────────────────────────────────────────────
  // Groups and roles are read INDEPENDENTLY from both tokens and then unioned.
  //
  // WHY: The spread merge above (`combined = { ...profile, ...atClaims }`) means
  // that if the access_token carries `groups: []` (empty array), it overwrites
  // the id_token's groups claim entirely — locking the user out even though the
  // id_token contained their real group memberships.
  //
  // By unioning instead of relying on the merged object, a user is granted access
  // if EITHER token carries their group — whichever token WSO2/Entra populates.
  //
  //   id_token  groups  (profile)   — from WSO2's OIDC id_token
  //   access_token groups (atClaims) — from WSO2's JWT access_token
  //   → UNION of both, deduplicated
  const idGroups  = normaliseToArray(profile.groups   || profile.group);
  const atGroups  = normaliseToArray(atClaims.groups  || atClaims.group);
  const groups    = [...new Set([...idGroups, ...atGroups])];

  const idRoles   = normaliseToArray(profile.roles    || profile.role);
  const atRoles   = normaliseToArray(atClaims.roles   || atClaims.role);
  const roles     = [...new Set([...idRoles, ...atRoles])];

  // ── Scopes ─────────────────────────────────────────────────────────────────
  const scopes = (combined.scope || '').split(' ').filter(Boolean);

  // ── Derived role (broad fallback) ──────────────────────────────────────────
  const derivedRole = deriveRoleFromClaims({ ...combined, roles, groups });

  return {
    sub,
    email,
    givenName,
    familyName,
    fullName,
    username,
    orgId,
    orgName,
    groups,
    roles,
    scopes,
    derivedRole,
    isAdmin: derivedRole === 'ADMIN',
    isStaff: derivedRole === 'ADMIN' || derivedRole === 'STAFF',
  };
}
